import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { SchemaFields } from "./FieldRenderer";
import {
  sectionsForArea,
  fieldsForArea,
  areFieldsComplete,
} from "./specSheetConfig";

const commonHeelOptions = {
  "3/12": '3-7/8"',
  "4/12": '3-15/16"',
  "5/12": '4-1/16"',
  "6/12": '4-3/16"',
  "7/12": '4-5/16"',
  "8/12": '4-7/16"',
  "9/12": '4-5/8"',
  "10/12": '4-13/16"',
  "11/12": '5"',
  "12/12": '5-3/16"',
};

const raisedHeelOptions = [
  "6(R - 13)",
  "8(R - 19)",
  "10(R - 25)",
  "12(R - 32)",
  "14(R - 38)",
  "16(R - 44)",
  "Other",
];

const RoofSection = (props) => {
  const [customRaisedHeel, setCustomRaisedHeel] = useState("");

  const sections = sectionsForArea(props.config, "roofs");
  const allFields = fieldsForArea(props.config, "roofs");

  // Auto-fill the common heel size from the selected roof pitch
  useEffect(() => {
    if (props.formValues["HeelHeight"] === "Common Heel") {
      const selectedPitch = props.formValues["RoofPitch"];
      if (commonHeelOptions[selectedPitch]) {
        props.setFormValues((prev) => ({
          ...prev,
          CommonHeelSize: commonHeelOptions[selectedPitch],
        }));
      }
    }
  }, [props.formValues["HeelHeight"], props.formValues["RoofPitch"]]);

  useEffect(() => {
    props.setIsComplete(areFieldsComplete(allFields, props.formValues));
  }, [props.formValues, props.config]);

  const handleChange = (fieldId, value) => {
    props.setFormValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  // Heel Height renders as a normal dropdown plus its conditional companions
  const specialRenderers = {
    heel_height: (field) => (
      <React.Fragment key={field.id}>
        <FormControl fullWidth required={field.required}>
          <InputLabel id={`${field.id}-label`}>{field.label}</InputLabel>
          <Select
            labelId={`${field.id}-label`}
            label={field.label}
            value={props.formValues[field.id] || ""}
            onChange={(e) => handleChange(field.id, e.target.value)}
          >
            {(field.options || []).map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {props.formValues["HeelHeight"] === "Common Heel" && (
          <FormControl fullWidth required>
            <InputLabel>Common Heel Size</InputLabel>
            <Select
              label="Common Heel Size"
              value={props.formValues["CommonHeelSize"] || ""}
              disabled
            >
              {Object.values(commonHeelOptions).map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {props.formValues.HeelHeight === "Raised Heel" && (
          <FormControl fullWidth required>
            <InputLabel id="RaisedHeel-label">Raised Heel Size</InputLabel>
            <Select
              labelId="RaisedHeel-label"
              label="Raised Heel Size"
              value={props.formValues.RaisedHeel || ""}
              onChange={(e) => handleChange("RaisedHeel", e.target.value)}
            >
              {raisedHeelOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {props.formValues.HeelHeight === "Raised Heel" &&
          props.formValues.RaisedHeel === "Other" && (
            <TextField
              fullWidth
              label="Specify Raised Heel Size"
              value={customRaisedHeel}
              onChange={(e) => setCustomRaisedHeel(e.target.value)}
            />
          )}
      </React.Fragment>
    ),
  };

  return (
    <div className="space-y-8">
      {sections.map((section) => (
        <div key={section.id}>
          <h3 className="app-muted mb-3 text-xs font-semibold uppercase tracking-wider">
            {section.title}
          </h3>
          <SchemaFields
            fields={section.fields}
            values={props.formValues}
            onChange={handleChange}
            columns={section.columns || 3}
            specialRenderers={specialRenderers}
          />
        </div>
      ))}
    </div>
  );
};

export default RoofSection;
